import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icon, Kbd, StopSplitButton } from '@/components/ui';
import { ModeSwitch } from './ModeSwitch';
import { CommandPalette } from './CommandPalette';
import { api } from '@/lib/api';
import { Lab } from '@/lib/toast';
import { useMediaQuery } from '@/lib/useMediaQuery';

export interface TopBarProps {
  title?: string;
  subtitle?: string;
  onLaunch?: () => void;
}

/**
 * Top bar. Left = page title + short description. Right = four
 * visually-harmonized controls (all 32 px tall) :
 *   1. Search stub (opens CommandPalette on click or ⌘K)
 *   2. Sim/Réel switch
 *   3. Stop split-button (only while any run is active)
 *   4. Launch button (accent)
 *
 * The global stop control is ALWAYS surfaced when at least one campagne is
 * running, regardless of the current route — operators can abort from any
 * page without having to navigate to Live first. Clicking the primary action
 * stops the live currently targeted by the URL if we are on /live, otherwise
 * jumps to Live first ; the chevron exposes the platform-wide kill switch.
 */
export function TopBar({ title, subtitle, onLaunch }: TopBarProps) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const { data: runs = [] } = useQuery({
    queryKey: ['runs', 'topbar-stop'],
    queryFn: () => api.listRuns({ limit: 30 }),
    refetchInterval: 2_000,  // faster detection for the emergency STOP button
  });
  const activeRuns = runs.filter((r) => r.status === 'running' || r.status === 'stopping');
  const activeCount = activeRuns.length;
  const currentLiveRun = location.pathname.startsWith('/live')
    ? activeRuns[0]?.run_id
    : undefined;

  const emergencyStopAll = async () => {
    const ok = await Lab.confirm(
      `Arrêt d'urgence : ${activeCount} campagne${activeCount > 1 ? 's ser' : ' ser'}${activeCount > 1 ? 'ont' : 'a'} tuée${activeCount > 1 ? 's' : ''} immédiatement. Confirmer ?`,
      "STOP D'URGENCE",
      { danger: true },
    );
    if (!ok) return;
    try {
      const res = await api.stopAllRuns('hard');
      Lab.toast(
        `${res.count} campagne${res.count > 1 ? 's' : ''} arrêtée${res.count > 1 ? 's' : ''} (kill immédiat).`,
        'pass',
      );
      invalidate();
    } catch (e) {
      Lab.toast(`Échec du stop d'urgence : ${(e as Error).message}`, 'error');
    }
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['runs'] });
  };

  const stopCurrent = async (mode: 'safe' | 'hard') => {
    // If we are on /live the Live page owns the stop action — don't double-fire.
    // Otherwise stop the most recent active run and navigate there so the user
    // watches the shutdown progression.
    const target = activeRuns[0];
    if (!target) return;
    const label = mode === 'safe' ? 'progressif' : 'immédiat';
    const ok = await Lab.confirm(
      `Arrêter la campagne ${target.run_id.slice(-8)} (arrêt ${label}) ?`,
      'Arrêter la campagne',
      { danger: mode === 'hard' },
    );
    if (!ok) return;
    try {
      await api.stopRun(target.run_id, mode);
      Lab.toast(`Campagne arrêtée (${mode}).`, 'pass');
      invalidate();
      if (!location.pathname.startsWith('/live')) {
        navigate(`/live?run=${target.run_id}`);
      }
    } catch (e) {
      Lab.toast(`Échec de l'arrêt : ${(e as Error).message}`, 'error');
    }
  };

  const stopAll = async (mode: 'safe' | 'hard') => {
    const label = mode === 'safe' ? 'progressif' : 'immédiat';
    const ok = await Lab.confirm(
      `Arrêter ${activeCount} campagne${activeCount > 1 ? 's' : ''} en cours sur toute la plateforme (arrêt ${label}) ?`,
      'Arrêt global',
      { danger: mode === 'hard' },
    );
    if (!ok) return;
    try {
      const res = await api.stopAllRuns(mode);
      Lab.toast(`${res.count} campagne${res.count > 1 ? 's' : ''} arrêtée${res.count > 1 ? 's' : ''} (${mode}).`, 'pass');
      invalidate();
    } catch (e) {
      Lab.toast(`Échec de l'arrêt global : ${(e as Error).message}`, 'error');
    }
  };

  // Champ de recherche stylisé : icône loupe à gauche, raccourci ⌘K à droite,
  // ouvre la CommandPalette au clic. Reste compact sur mobile (icône seule).
  const SearchControl = (
    <button
      type="button"
      onClick={() => setPaletteOpen(true)}
      title="Rechercher (⌘ K)"
      aria-label="Rechercher dans la plateforme"
      className="ctrl topbar-search"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        height: 32,
        padding: isMobile ? '0 10px' : '0 10px 0 12px',
        background: 'var(--surface-0)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md)',
        color: 'var(--text-muted)',
        cursor: 'pointer',
        minWidth: isMobile ? 'auto' : 200,
        flex: isMobile ? '0 0 auto' : '0 1 260px',
      }}
    >
      <Icon name="search" size={14} />
      {!isMobile && (
        <>
          <span style={{ flex: 1, textAlign: 'left', fontSize: 12, fontFamily: 'var(--font-sans)' }}>
            Rechercher…
          </span>
          <span style={{ display: 'inline-flex', gap: 2, flexShrink: 0 }}>
            <Kbd>⌘</Kbd>
            <Kbd>K</Kbd>
          </span>
        </>
      )}
    </button>
  );

  // Bouton Lancer : largeur définie, texte non coupable, hauteur cohérente.
  const LaunchControl = onLaunch && (
    <button
      type="button"
      onClick={onLaunch}
      className="ctrl primary"
      style={{
        height: 32,
        padding: '0 14px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        gap: 6,
      }}
    >
      <Icon name="play" size={12} />
      <span>Lancer</span>
    </button>
  );

  // Boutons d'arrêt — uniquement quand un run tourne.
  const StopControls = activeCount > 0 && (
    <>
      <button
        type="button"
        onClick={emergencyStopAll}
        title={`Stop d'urgence — tuer les ${activeCount} campagne${activeCount > 1 ? 's' : ''} en cours`}
        aria-label="Stop d'urgence"
        className="lab-emergency-stop"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          height: 32, padding: '0 12px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--danger-strong)',
          background: 'var(--danger)', color: 'var(--danger-fg)',
          fontWeight: 'var(--weight-bold)', fontSize: 'var(--text-sm)',
          letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase',
          cursor: 'pointer', flexShrink: 0,
          animation: 'lab-emergency-pulse 1.6s ease-in-out infinite',
        }}
      >
        <Icon name="stop" size={14} />
        {!isMobile && <span>Stop tout</span>}
        <span className="mono" style={{
          fontSize: 10, padding: '1px 6px', borderRadius: 'var(--radius-full)',
          background: 'color-mix(in oklab, var(--danger-fg) 25%, transparent)',
          color: 'var(--danger-fg)',
        }}>
          {activeCount}
        </span>
      </button>
      {!isMobile && (
        <StopSplitButton
          size="sm"
          label={location.pathname.startsWith('/live') ? 'Arrêter' : 'Arrêter le live'}
          onStopCurrent={stopCurrent}
          onStopAll={stopAll}
          activeCount={activeCount}
        />
      )}
    </>
  );

  // Layout mobile : titre seul sur ligne 1, contrôles ligne 2.
  if (isMobile) {
    return (
      <>
        <div className="topbar topbar-mobile">
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 4,
            padding: '8px 12px 8px 56px',  /* 56px laisse la place au burger fixé */
            borderBottom: '1px solid var(--border-subtle)',
            background: 'var(--surface-1)',
          }}>
            {/* Ligne 1 : titre + sous-titre + badge live */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
              {title && (
                <span style={{
                  fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  flex: '1 1 auto', minWidth: 0,
                }}>
                  {title}
                </span>
              )}
              {activeCount > 0 && (
                <span
                  className="badge run mono"
                  style={{ padding: '2px 6px', fontSize: 10, flexShrink: 0 }}
                  title={`${activeCount} campagne${activeCount > 1 ? 's' : ''} en cours`}
                >
                  {activeCount} live
                </span>
              )}
            </div>
            {subtitle && (
              <span style={{ fontSize: 11, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {subtitle}
              </span>
            )}
            {/* Ligne 2 : contrôles (search · mode · stop · lancer) */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {SearchControl}
              <ModeSwitch />
              <div style={{ flex: 1 }} />
              {StopControls}
              {LaunchControl}
            </div>
          </div>
        </div>
        <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      </>
    );
  }

  // Layout desktop : tout sur 1 ligne (titre à gauche, contrôles à droite).
  return (
    <>
      <div className="topbar">
        <div className="h-row" style={{ gap: 10, minWidth: 0, alignItems: 'baseline' }}>
          {title && <span className="page-title">{title}</span>}
          {subtitle && (
            <>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }} aria-hidden>·</span>
              <span className="page-sub" style={{ marginTop: 0 }}>{subtitle}</span>
            </>
          )}
          {activeCount > 0 && (
            <span
              className="badge run mono"
              style={{ marginLeft: 8, padding: '2px 8px', fontSize: 11 }}
              title={currentLiveRun ?? `${activeCount} campagne${activeCount > 1 ? 's' : ''} en cours`}
            >
              {activeCount} live
            </span>
          )}
        </div>

        <div className="sp" />

        {SearchControl}

        <ModeSwitch />

        {StopControls}

        {LaunchControl}
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </>
  );
}
